'use client';

import { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import { onAuthStateChange } from '@/lib/auth';
import { getDbInstance } from '@/lib/firebase';
import { UserProfile } from '@/types/user';

// プロフィール情報が完全かどうかをチェックする関数
const checkProfileComplete = (userData: UserProfile | null | undefined): boolean => {
  if (!userData) return false;

  // profileフィールドのチェック（基本情報のみ）
  const profile = userData.profile;

  if (
    !profile ||
    !profile.lastName ||
    !profile.firstName ||
    !profile.lastNameKana ||
    !profile.firstNameKana ||
    !profile.birthDate ||
    !profile.gender
  ) {
    return false;
  }

  return true;
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasUserData, setHasUserData] = useState<boolean | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;
    const AUTH_INIT_TIMEOUT_MS = 10000;
    const USER_DOC_TIMEOUT_MS = 7000;

    const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      try {
        return await Promise.race([
          promise,
          new Promise<T>((_, reject) => {
            timer = setTimeout(() => {
              reject(new Error(`${label} timed out after ${timeoutMs}ms`));
            }, timeoutMs);
          }),
        ]);
      } finally {
        if (timer) {
          clearTimeout(timer);
        }
      }
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 useAuth hook initializing...');
    }

    const authInitTimer = setTimeout(() => {
      if (!isMounted) return;
      console.warn('⏱️ Auth initialization timed out. Falling back to unauthenticated state.');
      setUser(null);
      setHasUserData(null);
      setIsProfileComplete(null);
      setLoading(false);
    }, AUTH_INIT_TIMEOUT_MS);

    const unsubscribe = onAuthStateChange(async (user) => {
      clearTimeout(authInitTimer);
      if (!isMounted) {
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Auth state changed:', {
          hasUser: !!user,
          userId: user?.uid,
          timestamp: new Date().toISOString(),
        });
      }

      setUser(user);

      if (user?.isAnonymous) {
        // A guest has no users/{uid} document and never will while anonymous; reading it would
        // only wait on a permission error. Nothing about a guest's profile is "incomplete".
        setHasUserData(false);
        setIsProfileComplete(false);
      } else if (user) {
        try {
          // ユーザーデータの確認
          console.log('👤 Fetching user data for:', user.uid);
          const userDocRef = doc(getDbInstance(), 'users', user.uid);
          const userDoc = await withTimeout(
            getDoc(userDocRef),
            USER_DOC_TIMEOUT_MS,
            'Fetching user profile document'
          );
          const exists = userDoc.exists();
          console.log('📄 User document exists:', exists);
          setHasUserData(exists);

          // プロフィール完了状況の確認
          if (exists) {
            const userData = userDoc.data() as UserProfile;
            const isComplete = checkProfileComplete(userData);
            console.log('✅ Profile complete:', isComplete);
            setIsProfileComplete(isComplete);
          } else {
            console.log('❌ User document not found');
            setIsProfileComplete(false);
          }
        } catch (error) {
          console.error('Error checking user data:', error);
          setHasUserData(false);
          setIsProfileComplete(false);
        }
      } else {
        console.log('❌ No authenticated user');
        setHasUserData(null);
        setIsProfileComplete(null);
      }

      console.log('🏁 Auth state processing complete, setting loading to false');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      clearTimeout(authInitTimer);
      unsubscribe();
    };
  }, []);

  // Three states, not two. A viewer who opened a share link is signed in anonymously so that
  // comments and the viewer count work; only a registered member counts as "logged in" for the
  // pages that require an account, the profile check and the member price.
  const isGuest = !!user && user.isAnonymous;
  const isRegistered = !!user && !user.isAnonymous;

  const authData = {
    user,
    loading,
    hasUserData,
    isGuest,
    isRegistered,
    isProfileComplete: isProfileComplete === true,
    isAuthenticated: isRegistered,
    needsProfileCompletion: isRegistered && (hasUserData === false || isProfileComplete === false),
    isDataLoading: loading || (!!user && (hasUserData === null || isProfileComplete === null)),
  };

  return authData;
};
